<template>
    <div v-if="editing" class="flex w-full space-x-1">
        <!-- Bind v-model to inputValue to track changes -->
        <Input v-bind="attrs" v-model="inputValue" @input:leave="() => setEditing(false)" autofocus />
        <!-- <XLightIcon class="h-4 w-4 cursor-pointer" @click="editing = false" /> -->
    </div>
    <div v-else class="flex space-x-1">
        <div
            :class="{ 'cursor-pointer': singleClick && editable }"
            @click="() => singleClick && setEditing(true)"
            @dblclick="() => !singleClick && setEditing(true)"
        >
            <slot />
        </div>

        <div v-if="$slots.icon">
            <slot name="icon" />
        </div>
        <div v-else-if="!iconless && editable">
            <PencilSquareIcon class="text-brand-base h-4 w-4 cursor-pointer" @click="() => setEditing(true)" />
        </div>
        <div v-else-if="!iconless && !editable">
            <LockClosedIcon class="text-brand-base h-4 w-4 cursor-not-allowed" />
        </div>
    </div>
</template>

<script setup>
import { useAttrs, ref, watch, onMounted, nextTick } from 'vue'
import Input from '@/components/form/Input.vue'
import { PencilSquareIcon, LockClosedIcon } from '@heroicons/vue/24/outline'
// import XLightIcon from '@/components/icons/XLightIcon.vue'

const props = defineProps({
    edit: { type: Boolean, default: false },
    iconless: { type: Boolean, default: false },
    editable: { type: Boolean, default: true },
    // Off by default: sessions, sensors and strides keep the double-click. The
    // program pages turn it on so a single tap opens the field.
    singleClick: { type: Boolean, default: false },
    modelValue: { type: [String, Number], default: '' }, // Assuming v-model is a String
})

const emits = defineEmits(['input:leave', 'input:start'])

// Accessing undeclared attributes
const attrs = useAttrs()

// Set inheritAttrs to false to prevent attributes being automatically added to the root element
defineOptions({
    inheritAttrs: false,
})

const editing = ref(false)
const inputValue = ref(props.modelValue) // Tracks the current input value
const initialInputValue = ref(props.modelValue) // Store the initial value for comparison

// Function to handle switching between editing modes
const setEditing = async (isEditing) => {
    if (isEditing && props.editable) {
        inputValue.value = props.modelValue
        initialInputValue.value = props.modelValue
        emits('input:start')
        await nextTick() // Ensure DOM is updated before focusing
    }

    // When editing ends
    if (!isEditing && editing.value) {
        const valueChanged = inputValue.value !== initialInputValue.value // Compare new value with initial
        emits('input:leave', { valueChanged }) // Emits input:leave with valueChanged boolean
    }
    if (props.editable) {
        editing.value = isEditing
    }
}

onMounted(() => {
    setEditing(props.edit)
})
</script>
